import { useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import PropTypes from 'prop-types';
import { Box, Skeleton } from '@mui/material';
import { AppChartCard } from '../@dashboard/app';
import useOptimizationData from '../../hooks/useOptimizationData';

const minH = { minHeight: 320 };

export default function OptimizationChart({
  site,
  startDate,
  endDate,
  msSessionInfo,
  expandContractChart,
  refreshKey,
}) {

  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });

  const { loading, series = [] } = useOptimizationData({
    site,
    msSessionInfo,
    startDate,
    endDate,
    inView: inView && !!startDate && !!endDate,
    refreshKey,
  });

  const hasData = useMemo(
    () => series.some((s) => (s?.data?.length ?? 0) > 0),
    [series]
  );

  if (!inView) {
    return <div ref={ref} style={minH} />;
  }

  if (loading) {
    return (
      <Box ref={ref} sx={{ minHeight: 320 }}>
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: 'background.paper',
            boxShadow: 1,
          }}
        >
          <Skeleton variant="text" width="45%" height={32} />
          <Skeleton variant="text" width="35%" height={24} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={242} sx={{ borderRadius: 2 }} />
        </Box>
      </Box>
    );
  }

  if (!hasData) {
    return <div ref={ref} style={minH} />;
  }

  return (
    <div ref={ref}>
      <AppChartCard
        title="Optimization Strategy"
        subheader="Battery Power (MW) and SOC (%)"
        xAxisLabel="Hour Beginning"
        expandContractChart={expandContractChart}
        doubleYAxis
        chartData={series}
        xMinMs={startDate?.valueOf?.()}
        xMaxMs={endDate?.valueOf?.()}
      />
    </div>
  );
}

OptimizationChart.propTypes = {
  site: PropTypes.object.isRequired,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  msSessionInfo: PropTypes.object.isRequired,
  expandContractChart: PropTypes.func,
  refreshKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
